const TCF_API_VERSION = 2;
const CONSENT_CHECK_INTERVAL_MS = 200;
const EMBED_CMP_RESPONSE_TIMEOUT_MS = 3000;
const READY_EVENT_STATUSES = ['tcloaded', 'useractioncomplete'];
const MZ_COOKIE_CONSENT_MODE = Object.freeze({
	NOT_REQUIRED: 'notRequired',
	LOCAL_CMP:    'localCmp',
	PARENT_CMP:   'parentCmp',
});
let consentDecisionPromise = null;

// Keeps MZ consent eligibility in one place while callers apply it at their appropriate UI lifecycle boundary.
const getMzCookieConsentMode = () => {
	const isManagerDomain = window.managerDomain && window.location.host === window.managerDomain;
	if (!window.isMz || isManagerDomain) return MZ_COOKIE_CONSENT_MODE.NOT_REQUIRED;

	return window.isEmbed ? MZ_COOKIE_CONSENT_MODE.PARENT_CMP : MZ_COOKIE_CONSENT_MODE.LOCAL_CMP;
};

const isConsentDecisionReady = (tcData, success) => {
	return success && tcData && READY_EVENT_STATUSES.includes(tcData.eventStatus);
};

// Finds the ancestor that owns the TCF locator without accidentally selecting a CMP inside the embed itself.
const findParentTcfApiFrame = () => {
	let currentFrame = window.parent;

	while (currentFrame) {
		try {
			if (currentFrame.frames.__tcfapiLocator) return currentFrame;
		} catch {
			// Cross-origin ancestors can reject property access while still allowing the standardized frame lookup.
		}

		if (currentFrame === window.top) break;
		currentFrame = currentFrame.parent;
	}

	return null;
};

const createTcfCallId = () => {
	if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();

	return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// Waits for the MZ CMP decision before Google IMA is initialized, using the parent CMP for framed embeds.
const waitForMzCookieConsent = () => {
	const consentMode = getMzCookieConsentMode();
	if (consentMode === MZ_COOKIE_CONSENT_MODE.NOT_REQUIRED) return Promise.resolve(true);
	if (consentDecisionPromise) return consentDecisionPromise;
	const usesParentCmp = consentMode === MZ_COOKIE_CONSENT_MODE.PARENT_CMP;
	const isFramedEmbed = usesParentCmp && window.self !== window.top;

	consentDecisionPromise = new Promise((resolve) => {
		let wasResolved = false;
		let consentCheckInterval = null;
		let consentTimeout = null;
		let localCmpListenerWasRegistered = false;
		let parentTcfApiFrame = null;
		let parentListenerId = null;
		const parentTcfApiFramesByCallId = new Map();

		const removeParentListener = () => {
			window.removeEventListener('message', handleParentTcfResponse);
			parentTcfApiFramesByCallId.clear();

			if (!parentTcfApiFrame || !parentListenerId) return;
			try {
				parentTcfApiFrame.postMessage({
					__tcfapiCall: {
						command:   'removeEventListener',
						parameter: parentListenerId,
						version:   TCF_API_VERSION,
						callId:    createTcfCallId(),
					},
				}, '*');
			} catch {
				// The parent can navigate while the player is waiting, making the previous frame unavailable.
			}
		};

		const resolveConsent = (consentDecisionWasReceived) => {
			if (wasResolved) return;

			wasResolved = true;
			clearInterval(consentCheckInterval);
			clearTimeout(consentTimeout);
			removeParentListener();

			if (localCmpListenerWasRegistered && typeof window.__cmp === 'function') {
				try {
					window.__cmp('removeEventListener', ['consent', handleLocalCmpConsent, false], null);
				} catch {
					// The CMP can be replaced during navigation after it has returned a decision.
				}
			}

			resolve(consentDecisionWasReceived);
		};

		const handleLocalCmpConsent = () => resolveConsent(true);

		function handleParentTcfResponse(messageEvent) {
			let response = messageEvent.data;
			if (typeof response === 'string') {
				try {
					response = JSON.parse(response);
				} catch {
					return;
				}
			}

			const tcfResponse = response?.__tcfapiReturn;
			if (!tcfResponse) return;

			const respondingParentFrame = parentTcfApiFramesByCallId.get(tcfResponse.callId);
			if (!respondingParentFrame || messageEvent.source !== respondingParentFrame) return;

			const tcData = tcfResponse.returnValue;
			if (tcfResponse.success && tcData) {
				// Once the parent CMP responds, wait for the visitor's decision instead of timing out behind its modal.
				parentTcfApiFrame = respondingParentFrame;
				clearInterval(consentCheckInterval);
				clearTimeout(consentTimeout);
				consentTimeout = null;
				parentTcfApiFramesByCallId.clear();
				// TCF addEventListener reuses its call ID when the visitor later accepts or declines.
				parentTcfApiFramesByCallId.set(tcfResponse.callId, respondingParentFrame);
			} else {
				parentTcfApiFramesByCallId.delete(tcfResponse.callId);
			}
			if (tcData?.listenerId) parentListenerId = tcData.listenerId;
			if (isConsentDecisionReady(tcData, tcfResponse.success)) resolveConsent(true);
		}

		const checkLocalCmpConsent = () => {
			if (typeof window.__cmp !== 'function') return;

			if (!localCmpListenerWasRegistered) {
				try {
					localCmpListenerWasRegistered = true;
					window.__cmp('addEventListener', ['consent', handleLocalCmpConsent, false], null);
				} catch {
					localCmpListenerWasRegistered = false;
					return;
				}
			}

			try {
				const consentStatus = window.__cmp('consentStatus', null, null, false);
				if (consentStatus?.userChoiceExists) resolveConsent(true);
			} catch {
				// The CMP stub can temporarily reject commands while the full implementation is loading.
			}
		};

		const registerParentTcfListener = () => {
			// Consentmanager can retire the locator after startup while keeping the top-level message handler active.
			const candidateParentFrame = findParentTcfApiFrame() || window.top;
			if (!candidateParentFrame) return;
			const parentCallId = createTcfCallId();

			try {
				parentTcfApiFramesByCallId.set(parentCallId, candidateParentFrame);
				candidateParentFrame.postMessage({
					__tcfapiCall: {
						command:   'addEventListener',
						parameter: null,
						version:   TCF_API_VERSION,
						callId:    parentCallId,
					},
				}, '*');
			} catch {
				parentTcfApiFramesByCallId.delete(parentCallId);
			}
		};

		const registerConsentListener = () => {
			if (isFramedEmbed) {
				registerParentTcfListener();
				return;
			}

			// Arena's own Consentmanager integration exposes its legacy API; a top-level embed has no consent source.
			if (consentMode === MZ_COOKIE_CONSENT_MODE.LOCAL_CMP) checkLocalCmpConsent();
		};

		// Parent TCF calls normally answer immediately; only a completely missing CMP should trigger this fallback.
		if (usesParentCmp) consentTimeout = setTimeout(() => resolveConsent(false), EMBED_CMP_RESPONSE_TIMEOUT_MS);
		if (isFramedEmbed) window.addEventListener('message', handleParentTcfResponse);
		consentCheckInterval = setInterval(registerConsentListener, CONSENT_CHECK_INTERVAL_MS);
		registerConsentListener();
	});

	return consentDecisionPromise;
};

export { getMzCookieConsentMode, MZ_COOKIE_CONSENT_MODE, waitForMzCookieConsent };
